import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { Eyebrow, Reveal, SectionTitle } from "./Primitives";
import { Cta } from "./Cta";

export const Faq = () => {
  const { content, t } = useSite();
  const faqs = content.faqs || [];
  if (!faqs.length) return null;
  return (
    <section id="faq" data-testid="faq-section" className="bg-bone py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <Eyebrow>{t("faq_eyebrow")}</Eyebrow>
          <SectionTitle>{t("faq_title")}</SectionTitle>
        </Reveal>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="border-t border-forest/15">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-forest/15">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left text-[17px] font-bold leading-snug text-forest hover:no-underline sm:text-[19px]">{f.q}</AccordionTrigger>
                <AccordionContent className="t-body pb-6 text-sage">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {content.disclaimer && <p data-testid="faq-disclaimer" className="mt-8 text-sm leading-relaxed text-sage">{content.disclaimer}</p>}
        </div>
      </div>
    </section>
  );
};

export const FinalCta = () => {
  const { content } = useSite();
  const f = content.final_cta || {};
  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden bg-forest py-20 text-bone grain lg:py-32">
      <div className="absolute -bottom-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(201,162,39,0.28),transparent)]" aria-hidden />
      <Reveal className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <h2 className="t-h2 mx-auto">{f.title}</h2>
        <p className="mx-auto mt-6 max-w-xl text-base text-white/80 sm:text-lg">{f.text}</p>
        <div className="mx-auto mt-10 grid max-w-xl gap-3">
          <Cta kind="consult" name="final_consultation" position="final" variant="gold" className="w-full" />
          <Cta kind="call" name="final_call" position="final" variant="light" className="w-full" />
        </div>
      </Reveal>
    </section>
  );
};

const POLICIES = ["privacy", "terms", "shipping", "refund"];

export const Footer = () => {
  const { content, contacts, t } = useSite();
  const b = content.brand || {};
  return (
    <footer data-testid="site-footer" className="bg-forest-deep pb-28 pt-16 text-white/80 md:pb-12">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 md:grid-cols-3">
        <div>
          <p className="text-xl font-extrabold text-bone">{b.brand_name}</p>
          <p className="mt-1 text-sm">{b.company_name}</p>
          <p className="mt-4 text-sm text-white/70">{b.tagline}</p>
        </div>
        <div className="space-y-2 text-sm">
          <p className="t-label text-gold">{t("footer_contact")}</p>
          <p><a data-testid="footer-phone" href={`tel:${contacts.phone}`} className="hover:text-white">{contacts.phone}</a></p>
          {b.email && <p><a data-testid="footer-email" href={`mailto:${b.email}`} className="hover:text-white">Support: {b.email}</a></p>}
          {b.address && <p data-testid="footer-address">Registered office: {b.address}</p>}
        </div>
        <nav aria-label="Policies" className="space-y-2 text-sm">
          <p className="t-label text-gold">{t("footer_policies")}</p>
          {POLICIES.map((slug) => (
            <p key={slug}><Link to={`/policy/${slug}`} data-testid={`footer-link-${slug}`} className="hover:text-white">{t(`policy_${slug}`)}</Link></p>
          ))}
        </nav>
      </div>
      <div className="mx-auto mt-12 max-w-7xl border-t border-white/10 px-5 pt-8 sm:px-8">
        <p data-testid="footer-disclaimer" className="text-sm leading-relaxed text-white/80">{content.disclaimer}</p>
        <p className="mt-4 text-sm text-white/80">© {new Date().getFullYear()} {b.company_name?.replace(/\.$/, "")}. {t("rights")}</p>
      </div>
    </footer>
  );
};

export const StickyBar = () => {
  const { t } = useSite();
  return (
  <div data-testid="sticky-mobile-cta" className="fixed inset-x-0 bottom-0 z-40 border-t border-forest/10 bg-white/90 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl md:hidden">
    <div className="grid grid-cols-[auto_1fr] gap-2">
      <Cta kind="call" name="call_sticky" position="sticky_bar" variant="outline" size="bar" label={t("sticky_call")} />
      <Cta kind="consult" name="consultation_sticky" position="sticky_bar" size="bar" label={t("nav_consult")} />
    </div>
  </div>
  );
};
